import { logger } from "../lib/logger";
import { ErrorFactory, logError } from "../lib/error-handling";
import { SupportTicket } from "./SupportTicketService";
import { supabase } from "../lib/supabase";
import { invokeSupabaseFunction } from "../lib/supabaseApi";

export interface StatusChangeNotification {
  ticketId: string;
  userEmail: string;
  ticketSubject: string;
  oldStatus: string;
  newStatus: string;
  assignedTo?: string;
  assignedEmail?: string;
  ticketUrl: string;
}

export class SupportTicketNotificationService {
  private static instance: SupportTicketNotificationService;

  private constructor() {}

  public static getInstance(): SupportTicketNotificationService {
    if (!SupportTicketNotificationService.instance) {
      SupportTicketNotificationService.instance =
        new SupportTicketNotificationService();
    }
    return SupportTicketNotificationService.instance;
  }

  /**
   * Notifie l'utilisateur d'un changement de statut de ticket
   */
  async notifyStatusChange(
    ticket: SupportTicket,
    oldStatus: string,
    newStatus: string,
    assignedEmail?: string,
  ): Promise<{ success: boolean; error?: string; emailId?: string }> {
    try {
      console.log("=== SUPPORT TICKET NOTIFICATION ===");
      console.log("Status change notification:", {
        ticketId: ticket.id,
        userEmail: ticket.email,
        oldStatus,
        newStatus,
        assignedEmail,
      });

      // Vérifier si c'est un changement de statut significatif
      if (!this.isSignificantStatusChange(oldStatus, newStatus)) {
        console.log("Status change not significant, skipping notification");
        return { success: true };
      }

      // Générer les données pour l'email
      const notificationData: StatusChangeNotification = {
        ticketId: ticket.id,
        userEmail: ticket.email,
        ticketSubject: ticket.subject,
        oldStatus,
        newStatus,
        assignedTo: ticket.assigned_to || undefined,
        assignedEmail,
        ticketUrl: `${window.location.origin}/admin?ticket=${ticket.id}`,
      };

      // Envoyer la notification via Edge Function (admin JWT requis)
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        logger.warn(
          "[SupportTicketNotificationService] Pas de session admin, skip status update email",
        );
        return { success: true };
      }
      const result = await invokeSupabaseFunction<{ emailId?: string }>(
        "send-ticket-status-update",
        notificationData,
        { timeout: 10000 },
      );
      console.log("Status notification sent successfully:", result);

      return {
        success: true,
        emailId: result.emailId,
      };
    } catch (error) {
      console.error("Failed to send status notification:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      logError(
        handleError(
          error,
          {
            component: "SupportTicketNotificationService",
            operation: "notifyStatusChange",
          },
          "Erreur lors de l'envoi de la notification de statut",
        ),
        {
          component: "SupportTicketNotificationService",
          operation: "notifyStatusChange",
        },
      );

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Détermine si un changement de statut justifie une notification
   */
  private isSignificantStatusChange(
    oldStatus: string,
    newStatus: string,
  ): boolean {
    const significantChanges = [
      { from: "open", to: "in_progress" }, // Prise en charge
      { from: "in_progress", to: "resolved" }, // Résolution
      { from: "resolved", to: "closed" }, // Fermeture
      { from: "in_progress", to: "closed" }, // Fermeture directe
    ];

    return significantChanges.some(
      (change) => change.from === oldStatus && change.to === newStatus,
    );
  }

  /**
   * Génère le message de statut en français
   */
  private getStatusLabel(status: string): string {
    const statusLabels = {
      open: "Ouvert",
      in_progress: "En cours",
      resolved: "Résolu",
      closed: "Fermé",
    };
    return statusLabels[status as keyof typeof statusLabels] || status;
  }

  /**
   * Génère le sujet de l'email selon le changement de statut
   */
  private generateEmailSubject(
    oldStatus: string,
    newStatus: string,
    ticketSubject: string,
  ): string {
    const transitions = {
      "open->in_progress":
        "Votre ticket DooDates est maintenant pris en charge !",
      "in_progress->resolved": "Votre ticket DooDates a été résolu !",
      "resolved->closed": "Votre ticket DooDates est maintenant fermé",
      "in_progress->closed": "Votre ticket DooDates est maintenant fermé",
    };

    const transition = `${oldStatus}->${newStatus}`;
    const baseSubject =
      transitions[transition as keyof typeof transitions] ||
      "Mise à jour de votre ticket DooDates";

    return `${baseSubject}: ${ticketSubject}`;
  }
}

// Helper function pour la gestion d'erreurs
function handleError(
  error: unknown,
  context: { component: string; operation: string },
  userMessage: string,
): Error {
  if (error instanceof Error) {
    return ErrorFactory.network(
      `${userMessage}: ${error.message}`,
      userMessage,
    );
  }
  return ErrorFactory.network(userMessage);
}
